import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { ReceiptLongOutlined, Search } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import financeService from '../../services/financeService';
import { AccountLedgerResponse, ACCOUNT_TYPE_LABEL, JOURNAL_SOURCE_OPTIONS } from '../../types/finance';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDrCr, formatPaisa } from '../../utils/paisa';
import DateRangePicker, { DateRange } from '../../components/common/DateRangePicker';
import SearchableSelect from '../../components/common/SearchableSelect';
import DownloadMenu from '../../components/common/DownloadMenu';
import EmptyState from '../../components/common/EmptyState';
import JournalEntryDrawer from '../../components/finance/journal/JournalEntryDrawer';
import AccountLedgerTable from '../../components/finance/ledger/AccountLedgerTable';
import { useChart } from '../../components/finance/ledger/useChart';
import { ledgerPath } from '../../components/finance/ledger/ledgerPath';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import AccountLedgerDocument from '../../components/finance/pdf/AccountLedgerDocument';

const DIRECTIONS = [{ value: 'in', label: 'Debits' }, { value: 'out', label: 'Credits' }];

/**
 * Finance → Ledger: one account's history — the balance before the dates,
 * every line with the balance after it, and the balance at the end. Opened
 * from the chart, the trial balance, statements and vouchers, or by picking
 * an account. The account and dates live in the URL.
 */
const AccountLedgerPage: React.FC = () => {
  const navigate = useNavigate();
  const { accountId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { options } = useChart();
  const from = params.get('from');
  const to = params.get('to');
  const range: DateRange = useMemo(() => ({ from, to }), [from, to]);
  const [direction, setDirection] = useState<'in' | 'out' | ''>('');
  const [sourceType, setSourceType] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState<AccountLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const search = useDebounce(q, 300);
  const filters = useMemo(() => ({ from, to, direction, sourceType, q: search }), [from, to, direction, sourceType, search]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      setData(await financeService.getAccountLedger(accountId, { ...filters, page: page + 1, limit }));
      setError(null);
    } catch (e: any) {
      setData(null);
      setError(e?.message || 'Could not load the ledger.');
    } finally {
      setLoading(false);
    }
  }, [accountId, filters, page, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(0); }, [accountId, filters]);
  // A voucher's account opens its ledger here; the voucher closes behind it.
  useEffect(() => { setOpenEntryId(null); }, [accountId]);

  const setRange = (r: DateRange) => navigate(ledgerPath(accountId, r), { replace: true });
  const setAccount = (id: string) => navigate(ledgerPath(id, range));
  const filtered = !!(direction || sourceType || q);
  const reset = () => { setDirection(''); setSourceType(''); setQ(''); setParams({}, { replace: true }); };
  const shown = data?.account._id === accountId ? data : null;

  // The PDF is drawn here from every line the filters match; the CSV comes from the server.
  const { currency } = useAuth();
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const exportAs = async (kind: 'pdf' | 'csv') => {
    if (kind === 'csv') return financeService.exportAccountLedgerCsv(accountId, filters);
    let code = '';
    await generatePdf(async () => {
      const report = await financeService.getAccountLedgerReport(accountId, filters);
      code = report.account.code;
      return <AccountLedgerDocument report={report} currency={currency || 'PKR'} branding={branding} generatedAt={new Date().toISOString()} />;
    }, () => `Ledger_${code}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  const figure = (label: string, value: string, hint?: string) => (
    <Box sx={{ flex: 1, minWidth: 150 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Ledger</Typography>
          <Typography variant="body2" color="text.secondary">
            Everything posted to one account, with the balance after each line.
          </Typography>
        </Box>
        {accountId && <DownloadMenu onExport={exportAs} />}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect label="Account" value={accountId} onChange={setAccount} options={options.all} placeholder="Choose an account" minWidth={300} />
          <DateRangePicker value={range} onChange={setRange} label="Dates" minWidth={220} />
          {accountId && (
            <>
              <SearchableSelect label="Side" value={direction} onChange={v => setDirection(v as 'in' | 'out' | '')} options={DIRECTIONS} placeholder="Debits and credits" minWidth={170} />
              <SearchableSelect label="Source" value={sourceType} onChange={setSourceType} options={JOURNAL_SOURCE_OPTIONS} placeholder="All sources" minWidth={170} />
              <TextField
                size="small" placeholder="Search voucher or description…" value={q} onChange={e => setQ(e.target.value)} sx={{ flex: 1, minWidth: 180 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
              />
              {(filtered || range.from || range.to) && <Button size="small" onClick={reset}>Reset</Button>}
            </>
          )}
        </Stack>
      </Paper>

      {!accountId ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <EmptyState
            icon={<ReceiptLongOutlined />}
            title="Choose an account"
            description="Pick any account — or a group, to see every account beneath it. Balances in the chart, trial balance and statements open here too."
          />
        </Paper>
      ) : (
        <>
          {shown ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <Box component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>{shown.account.code}</Box>
                <Box component="span" fontWeight={700} color="text.primary">{shown.account.name}</Box>
                {' · '}{ACCOUNT_TYPE_LABEL[shown.account.type]}{shown.account.isGroup ? ' group — every account beneath it' : ''}
                {' · normally a '}{shown.account.normalBalance === 'DEBIT' ? 'debit' : 'credit'} balance
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />}>
                {figure('Opening', formatDrCr(shown.openingPaisa), range.from ? 'Before these dates' : 'Since the books began')}
                {figure('Debits', formatPaisa(shown.totals.debitPaisa), filtered ? 'Matching lines' : undefined)}
                {figure('Credits', formatPaisa(shown.totals.creditPaisa), filtered ? 'Matching lines' : undefined)}
                {figure('Closing', formatDrCr(shown.closingPaisa), range.to ? 'At the end of these dates' : 'Today')}
              </Stack>
            </Paper>
          ) : loading && <Skeleton variant="rounded" height={110} sx={{ borderRadius: 2, mb: 2 }} />}

          <AccountLedgerTable
            data={shown}
            loading={loading}
            error={error}
            filtered={filtered}
            page={page}
            limit={limit}
            onPage={setPage}
            onLimit={l => { setLimit(l); setPage(0); }}
            onOpen={setOpenEntryId}
          />
        </>
      )}

      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={() => setRefreshKey(k => k + 1)} />
    </Box>
  );
};

export default AccountLedgerPage;
